import React from 'react';
import { useTranslation } from 'react-i18next';

import { useCurrentUser } from '@modules/user/hooks';
import { UserGrantActionToken, UserGrantCategoryToken } from '@modules/types/graphql';
import { TableCellResult } from '@modules/stats/atoms';
import { Table, TableCell, TableRow } from '@modules/layout/organisms';
import { StudentSubjectResultsDrawer } from '@modules/students/organisms';

import type { StudentSubjectResultsDrawerProps } from '@modules/students/organisms';
import type { OnlineStudentPerformanceSubjectEntity } from '@modules/students/entities';
import type { TableCellType } from '@modules/layout/organisms';

type StudentPerformanceTableProps = {
    courseId: string;
    userId: string;
    performances: OnlineStudentPerformanceSubjectEntity[];
};

const columns: TableCellType[] = [
    {
        id: 'subjectName',
        label: 'common:table.labels.subject',
        width: 300,
    },
    {
        id: 'startedAt',
        label: 'common:table.labels.started_at_test',
    },
    {
        id: 'finishedAt',
        label: 'common:table.labels.finished_at_test',
    },
    {
        id: 'scoreInPoints',
        label: 'common:table.labels.result_score',
    },
    {
        id: 'score',
        label: 'common:table.labels.result_score_absolute',
    },
];

const StudentPerformanceTable = (props: StudentPerformanceTableProps): React.ReactElement => {
    const { courseId, userId, performances } = props;

    const { t } = useTranslation('common');

    const [openDrawerResults, setOpenDrawerResults] = React.useState(false);
    const [chosenSubjectData, setChosenSubjectData] = React.useState<
        StudentSubjectResultsDrawerProps['subject'] | undefined
    >(undefined);

    const { currentUser } = useCurrentUser({ fetchPolicy: 'network-only' });
    const { studentGrants } = currentUser.can({
        [UserGrantCategoryToken.student]: [UserGrantActionToken.get],
    });

    const handleCloseResults = React.useCallback(() => {
        setOpenDrawerResults(false);
    }, []);

    const handleOpenResults = React.useCallback(
        (performance: OnlineStudentPerformanceSubjectEntity) => () => {
            setOpenDrawerResults(true);
            setChosenSubjectData({
                id: performance.subjectId,
                name: performance.subjectName,
                score: performance.scoreInPoints,
            });
        },
        [],
    );

    if (!studentGrants.canGet) {
        return <>{null}</>;
    }

    return (
        <>
            <Table
                freezeFirstColumn
                freezeLastColumn
                disabledPagination
                id='performances-table'
                total={{
                    title: [
                        t('common:blocks.subject_summary.one', { summary: '%d' }),
                        t('common:blocks.subject_summary.some', { summary: '%d' }),
                        t('common:blocks.subject_summary.multiple', { summary: '%d' }),
                    ],
                    value: performances.length,
                }}
                columns={columns}
            >
                {performances.map(performance => (
                    <TableRow key={performance.id}>
                        <TableCell>{performance.subjectName}</TableCell>

                        <TableCell>{performance.getFormattedStartedAt()}</TableCell>

                        <TableCell>{performance.getFormattedFinishedAt()}</TableCell>

                        <TableCell>
                            <TableCellResult
                                isSuccess={performance.resultSuccess}
                                onClick={
                                    performance.score ? handleOpenResults(performance) : undefined
                                }
                            >
                                {performance.getFormattedScoreInPoints()}
                            </TableCellResult>
                        </TableCell>

                        <TableCell>
                            <TableCellResult isSuccess={performance.resultSuccess}>
                                {performance.score}
                            </TableCellResult>
                        </TableCell>
                    </TableRow>
                ))}
            </Table>

            <StudentSubjectResultsDrawer
                open={openDrawerResults}
                subject={chosenSubjectData}
                courseId={courseId}
                userId={userId}
                onClose={handleCloseResults}
            />
        </>
    );
};

export { StudentPerformanceTable };
